import { useMemo } from "react";
import type { Lead } from "@/types/lead";
import { countFollowUpsNeedingAttention } from "@/lib/lead/followUpAttention";

/** Follow-up lists for Sales Console (open / overdue / upcoming) plus attention count aligned with daily checklist. */
export function useSalesConsoleLeadBuckets(leads: Lead[]) {
  const followUpsNeedingAttentionCount = useMemo(() => countFollowUpsNeedingAttention(leads), [leads]);
  const needsReviewLeads = useMemo(
    () => leads.filter((l) => l.needs_review).sort((a, b) => b.lastUpdated - a.lastUpdated),
    [leads]
  );

  const { openFollowUps, overdue, upcoming } = useMemo(() => {
    const now = Date.now();
    const followUps = leads.flatMap((lead) =>
      (lead.follow_ups ?? []).map((f) => ({
        ...f,
        lead,
        overdue: f.status === "open" && Date.parse(f.due_date) < now,
      }))
    );
    const open = followUps.filter((f) => f.status === "open");
    return {
      openFollowUps: open,
      overdue: open.filter((f) => f.overdue),
      upcoming: open.filter((f) => !f.overdue),
    };
  }, [leads]);

  return { followUpsNeedingAttentionCount, needsReviewLeads, openFollowUps, overdue, upcoming };
}
